/**
 * Inputs with inline validation.
 *
 * ## The error shape is a data structure, not a string
 *
 * `components.md`: *"the rule → the likely cause → the one-click fix"*, always
 * in that order, and *"the fix button carries the corrected value so it doubles
 * as the explanation."* A component taking `error: string` cannot enforce any
 * of that — it can only hope. So {@link InvalidState} takes the three parts
 * separately and renders them in order, which makes an error missing its cause
 * a **compile** error rather than a copy review.
 *
 * The fix carrying the value is the part that is easy to lose. "Fix check
 * digit" is a button; **"Change to 09506000134376"** is a button that has
 * already explained itself, and a user who does not want it now knows exactly
 * what they were being offered.
 *
 * ## The valid message does NOT use `--pp-live`, and that is a correction
 *
 * `components.md` line 49 specifies *"trailing `✓ Check digit valid` in
 * `--pp-live`"*. Measured, `--pp-live` on white is **4.32:1** — below AA for
 * 12–13px text. The design's own palette carries the answer:
 * `--pp-pill-live-fg` is the same green darkened to **6.47:1** on the same
 * background, which is what the `-fg` tokens exist for.
 *
 * The border stays `--pp-live`: as a border it is a non-text signal, where
 * WCAG 1.4.11 asks 3:1 and it clears comfortably. Same colour family, two
 * jobs, two floors — the distinction `acceptance-tests.md` line 23 currently
 * collapses and that `tokens.css`'s amended footer now spells out.
 *
 * ## Marking the character, not reddening the field
 *
 * A wholly red field says "everything here is wrong"; one marked character says
 * "this digit". `markFrom`/`markTo` render the offending span on
 * `--pp-pill-error-bg` over the *displayed* value, which is why the value is
 * echoed beneath the control rather than restyled inside it — a text input
 * cannot carry a highlight on part of its own value.
 */
import { createElement } from "react";
function markedValue(value, from, to) {
    return [
        value.slice(0, from),
        createElement("mark", { key: "m", className: "pp-input-mark" }, value.slice(from, to)),
        value.slice(to),
    ];
}
export function Input({ label, value, mono = false, validation, onFix, className, id, ...rest }) {
    const state = validation?.state;
    const describedBy = id === undefined ? undefined : `${id}-validation`;
    const control = createElement("input", {
        ...rest,
        id,
        value,
        className: "pp-input-control",
        "data-mono": mono ? "true" : undefined,
        "aria-invalid": state === "invalid" ? "true" : undefined,
        "aria-describedby": validation === undefined ? undefined : describedBy,
    });
    const children = [
        createElement("span", { key: "l", className: "pp-input-label" }, label),
        control,
    ];
    if (validation?.state === "valid") {
        children.push(createElement("p", { key: "v", id: describedBy, className: "pp-input-valid" }, `✓ ${validation.message}`));
    }
    if (validation?.state === "invalid") {
        const { rule, cause, fix, markFrom, markTo } = validation;
        const parts = [
            createElement("span", { key: "r", className: "pp-input-rule" }, rule),
            createElement("span", { key: "c", className: "pp-input-cause" }, cause),
        ];
        if (markFrom !== undefined && markTo !== undefined) {
            parts.push(createElement("span", {
                key: "s",
                className: "pp-input-supplied",
                "data-mono": mono ? "true" : undefined,
            }, ...markedValue(value, markFrom, markTo)));
        }
        if (fix !== undefined) {
            parts.push(createElement("button", {
                key: "f",
                type: "button",
                className: "pp-input-fix",
                // The corrected value IS the label, so the button explains itself.
                onClick: onFix === undefined ? undefined : () => onFix(fix.value),
            }, fix.label));
        }
        children.push(createElement("div", { key: "i", id: describedBy, className: "pp-input-invalid", role: "alert" }, ...parts));
    }
    return createElement("label", {
        className: className === undefined ? "pp-input" : `pp-input ${className}`,
        "data-state": state,
    }, ...children);
}
//# sourceMappingURL=Input.js.map